import { ApiDocumentation } from '@/types/api';

const logger = {
  info: (message: string, ...args: any[]) => console.log(`[INFO] ${message}`, ...args),
  error: (message: string, ...args: any[]) => console.error(`[ERROR] ${message}`, ...args),
};

interface ApiCollectionData {
  fileName: string;
  data: ApiDocumentation;
}

// Load all JSON files from data directory
const loadLocalJsonFiles = async (): Promise<ApiCollectionData[]> => {
  const results: ApiCollectionData[] = [];
  
  try {
    logger.info('Loading local JSON files from data directory...');

    const context = require.context('@/data', false, /\.json$/);
    const fileKeys = context.keys();
    
    logger.info(`Found ${fileKeys.length} JSON files in data directory`);
    
    for (const key of fileKeys) {
      try {
        const module = context(key);
        const data = (module.default || module) as ApiDocumentation;
        const fileName = key.replace('./', ''); 
    
        if (data && data.apis && Array.isArray(data.apis)) {
          results.push({ fileName, data });
          logger.info(`Successfully loaded local file: ${fileName} (${data.apis.length} APIs)`);
        } else {
          logger.info(`File ${fileName} doesn't contain valid API documentation structure`);
        }
      } catch (error) {
        logger.error(`Failed to load ${key}:`, error);
      }
    }
    
    logger.info(`Successfully loaded ${results.length} local JSON files`);
  } catch (error) {
    logger.error('Error loading local JSON files using require.context:', error);
    logger.info('Unable to dynamically load local files in this environment');
  }
  
  return results;
};

const getAllApiCollections = async (): Promise<ApiCollectionData[]> => {
  logger.info('Loading API collections from local data directory');
  return await loadLocalJsonFiles();
};

const createCollectionFromFileName = (fileName: string): { id: number; name: string } => {
  // Generate a consistent ID based on filename
  const generateId = (name: string): number => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  };

  // Clean up filename to create display name
  const cleanName = fileName
    .replace('.json', '')           // Remove .json extension
    .replace(/[-_]/g, ' ')          // Replace dashes and underscores with spaces
    .replace(/'/g, '')              // Remove apostrophes
    .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize first letter of each word
    .replace(/\s+/g, ' ')           // Normalize spaces
    .trim();

  // Add "APIs" suffix if not already present
  const displayName = cleanName.endsWith('APIs') ? cleanName : `${cleanName}`;
  
  return { 
    id: generateId(fileName),
    name: displayName
  };
};

const ApiDataService = {
  // Get all API collections from local data directory
  getAllCollections: async () => {
    try {
      const collections = await getAllApiCollections();
        const orderMap: Record<string, number> = {
          'AUTHENTICATION.json': 1,
          'SPOT AUTHENTICATION.json': 5,
          'PORTFOLIO.json': 2,
          'RESERVATION.json': 3,
          'HISTORIC.json': 4,
          'SPOT REALTIME.json': 6,
        };
      
      // Sort collections according to the defined order
      const sortedCollections = collections.sort((a, b) => {
        const orderA = orderMap[a.fileName] || 999;
        const orderB = orderMap[b.fileName] || 999;
        return orderA - orderB;
      });
      
      return sortedCollections.map(({ fileName, data }) => ({
        ...createCollectionFromFileName(fileName),
        data,
      }));
    } catch (error) {
      logger.error('error fetching all collections', error);
      throw error;
    }
  },
};

export default ApiDataService;
